/**
 * Standard door and window sizes as quick picks, and the door's hinge side
 * (issue 160).
 *
 * Swedish doors and windows are sold by modulmått in decimetres: a "7×19"
 * door is built for a 700 × 1900 mm wall opening and its frame (karm) is
 * 10 mm smaller each way — Harvia's 7×19 glass door is 690 × 1890 mm
 * (harvia.com, D71904M: 27.17 in × 6.2 ft), and the Diplomat modulmått table
 * lists 7×19 → karmyttermått 690 × 1890. `WallOpening.widthMm/heightMm` is the
 * rough opening between the jack studs and under the header
 * (`generateFramedWall`), so the preset IS the module: 7×19 → 700 × 1900.
 */
import type {
	DoorHingeSide,
	DoorSwing,
	WallOpening,
} from "../framing/generateFramedWall";
import type { InteriorWall } from "../interior/types";

export interface OpeningSizePreset {
	/** Module name as printed on the product, "7×19". */
	id: string;
	widthMm: number;
	heightMm: number;
}

const moduleSize = (widthDm: number, heightDm: number): OpeningSizePreset => ({
	id: `${widthDm}×${heightDm}`,
	widthMm: widthDm * 100,
	heightMm: heightDm * 100,
});

/**
 * 7×19 and 8×19 are the stock bastudörr sizes (Harvia, Tylö); 9×19 is the
 * seeded front door; 9×21 is a full-height entrance door, which only fits once
 * the wall is raised above 2.1 m — validateOpeningSet says so if it doesn't.
 */
export const DOOR_SIZE_PRESETS: readonly OpeningSizePreset[] = [
	moduleSize(7, 19),
	moduleSize(8, 19),
	moduleSize(9, 19),
	moduleSize(9, 21),
];

/** Small square, the default window, and a large square view window. */
export const WINDOW_SIZE_PRESETS: readonly OpeningSizePreset[] = [
	moduleSize(6, 6),
	moduleSize(9, 7),
	moduleSize(10, 10),
];

export function openingSizePresets(
	type: WallOpening["type"],
): readonly OpeningSizePreset[] {
	return type === "door" ? DOOR_SIZE_PRESETS : WINDOW_SIZE_PRESETS;
}

/** The preset the opening's size matches exactly, or null for a custom size. */
export function matchOpeningSizePreset(
	opening: Pick<WallOpening, "type" | "widthMm" | "heightMm">,
): OpeningSizePreset | null {
	return (
		openingSizePresets(opening.type).find(
			(preset) =>
				preset.widthMm === opening.widthMm &&
				preset.heightMm === opening.heightMm,
		) ?? null
	);
}

export function resolveHingeSide(
	opening: Pick<WallOpening, "hingeSide">,
): DoorHingeSide {
	return opening.hingeSide === "right" ? "right" : "left";
}

/**
 * Whether the hinge jamb is the opening's low-`xMm` edge.
 *
 * `hingeSide` is seen from outside; `xMm` runs from the wall's low-coordinate
 * corner (−X for front/back, −Z for left/right). Standing outside the front
 * wall you face −Z, so your left hand points to −X: the low edge. Outside the
 * back wall you face +Z and your left is +X: the high edge. Outside the left
 * wall you face +X and your left is −Z (low); outside the right wall, +Z (high).
 */
export function hingeAtLowEdge(
	wall: InteriorWall,
	hingeSide: DoorHingeSide,
): boolean {
	const leftIsLow = wall === "front" || wall === "left";
	return hingeSide === "left" ? leftIsLow : !leftIsLow;
}

export function resolveDoorSwing(
	opening: Pick<WallOpening, "swing">,
): DoorSwing {
	return opening.swing === "in" ? "in" : "out";
}
